import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import { AiTrackingOrigin } from '@databox/types/domains/ai/chat.enums';

import type {
	APIData,
	BlockThemeConfig,
	BoardPreviewMap,
	CreateRecordProperties,
	PaginationDirection,
	PopupData,
	RecordData,
	RequestError,
	Service,
	SourcesMap,
	TemplateResolve,
} from 'webapp-types';
import { showTemplateCreationResult } from 'webapp/const/template-resolve';
import ThemeConfig from 'webapp/const/theme-config';
import {
	BatchSelectActionType,
	BillingFeature,
	PopupContextSwitchAction as ContextSwitchAction,
	ModelName,
	PopupEvent,
	TemplateObjectType,
	Theme,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { NotificationCreateType } from 'webapp/enums/notifications';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getGradient } from 'webapp/utils/color';
import {
	getPreviousOrNextItemByDirection,
	getResourceSizeFromUrl,
	MD_BREAKPOINT,
	sortBy,
	uniq,
} from 'webapp/utils/common';

import BoardLoopsPopupBoardToLoopComponent from 'webapp/components/board-loops/popup-board-to-loop';
import BoardPopupChooseComponent from 'webapp/components/board/popup-choose';
import BoardPopupDeleteConfirmComponent from 'webapp/components/board/popup-delete-confirm';
import BoardPopupDuplicateComponent from 'webapp/components/board/popup-duplicate';
import type {
	MigrationMap,
	MigrationMode,
	MigrationResponse,
} from 'webapp/components/board/popup-migration-tool';
import BoardPopupMigrationToolComponent from 'webapp/components/board/popup-migration-tool';
import BoardPopupPreviewComponent from 'webapp/components/board/popup-preview';
import BoardPopupResolveDataComponent from 'webapp/components/board/popup-resolve-data';
import BoardPopupShareComponent from 'webapp/components/board/popup-share';
import BoardPopupTransferComponent from 'webapp/components/board/popup-transfer';
import BoardPopupVisualSettingsComponent from 'webapp/components/board/popup-visual-settings';
import BoardsPopupSelectListComponent from 'webapp/components/boards/popup-select-list';
import CommonPopupAccessPermissionsComponent from 'webapp/components/common/popup-access-permissions';
import DatawallPopupFullComponent from 'webapp/components/datawall/popup-full';
import ReportsPopupBoardToReportComponent from 'webapp/components/reports/popup-board-to-report';

import type { BoardPreviewRecord } from 'webapp/models';
import type { TrackingOriginForDataboardDuplicate } from 'webapp/models-custom/board';
import Board from 'webapp/models-custom/board';
import type InfolayerItemGeneral from 'webapp/models-custom/infolayer-item/general';
import type Metric from 'webapp/models-custom/metric';
import type PopupModal from 'webapp/models-custom/popup/modal';
import type Template from 'webapp/models-custom/template';
import type BoardRecord from 'webapp/models/board';
import { DEFAULT_BG_IMAGE_OPACITY } from 'webapp/models/board';

import { Severity } from 'webapp/services/logger';
import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { TagCountList } from 'webapp/services/model-manager/tags';
import type { TrackingOriginForTemplateUsed } from 'webapp/services/model-manager/templates';

export type TrackingOriginType =
	| 'home'
	| 'databoards'
	| 'designer'
	| 'share popup'
	| 'recommended_snapshots'
	| 'setup'
	| 'notifications'
	| `${AiTrackingOrigin}_AIchat`
	| AiTrackingOrigin.GENIE;
export type TrackingOriginTypeForPreview =
	| 'page'
	| 'home'
	| 'databoards'
	| 'recommended_templates'
	| 'sidebar'
	| `${AiTrackingOrigin}_AIchat`
	| 'notifications'
	| 'popup_usages';
export type TrackingOriginTypeForAddReport =
	| 'page'
	| 'home'
	| 'databoards'
	| 'designer'
	| `${AiTrackingOrigin}_AIchat`
	| AiTrackingOrigin.GENIE;
export type TrackingOriginTypeForAddLoop =
	| 'page'
	| 'home'
	| 'databoards'
	| 'designer_bar'
	| `${AiTrackingOrigin}_AIchat`
	| AiTrackingOrigin.GENIE;
export type TrackingOriginForTemplateCreated =
	| 'databoards'
	| 'designer'
	| 'account_templates'
	| 'board_preview'
	| 'account_progress'
	| 'designer_board_pager'
	| 'recommended_templates'
	| 'recommended_account_templates'
	| 'setup';
export type TrackingOriginForDataboardCreatedNew =
	| 'designer'
	| 'designer_sidebar'
	| 'greenplus'
	| 'public_template'
	| 'public_template_home'
	| 'wizard'
	| 'wizard_home'
	| 'wizard_sidebar';

interface RequestedBoardData {
	id: string;
	requestedTimestamp: number;
}

export default class ModelManagerBoardsService extends ModelManagerForStoreService<
	BoardPreviewRecord | BoardRecord,
	Board
> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('connect') declare connectService: Service.Connect;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('logger') declare loggerService: Service.Logger;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('billing') declare billingService: Service.Billing;
	@service('popup') declare popupService: Service.Popup;
	@service('socket') declare socketService: Service.Socket;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('navigation-main') declare navigationMainService: Service.NavigationMain;
	@service('account') declare accountService: Service.Account;
	@service('constants') declare constantsService: Service.Constants;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('wizard') declare wizardService: Service.Wizard;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('verification') declare verificationService: Service.Verification;

	private _boardModels: Board[] = [];

	readonly defaultBgImageOpacity = DEFAULT_BG_IMAGE_OPACITY;

	@cached
	get boardModels(): Board[] {
		this.updateModelCache(this._boardModels, this.savedRecords);
		return this._boardModels;
	}

	@cached
	get normalBoardModels(): Board[] {
		return this.normalBoardModelsWithDeleted.filter((board: Board) => !board.deleted);
	}

	@cached
	get normalBoardModelsWithDeleted(): Board[] {
		// also includes public templates in the public template space
		return this.boardModels.filter((board: Board) => {
			const template = board.templateModel;

			return (
				!board.templateId ||
				(this.accountService.isPublictemplatesSpace &&
					template?.isPublic &&
					template.objectType === TemplateObjectType.BOARD)
			);
		});
	}

	@cached
	get accountTemplateBoardModels(): Board[] {
		return this.boardModels.filter((board: Board) => {
			const template = board.templateModel;

			return (
				!!template && template.objectType === TemplateObjectType.BOARD && !template.isPublic
			);
		});
	}

	get assignedTagsForBoards(): TagCountList[] {
		const tags = this.normalBoardModels.reduce((acc: number[], model: Board) => {
			acc.push(...model.tags);
			return acc;
		}, []);
		return this.mmTagsService.getTagCountList(tags);
	}

	get assignedTagsForAccountTemplates(): TagCountList[] {
		const tags = this.accountTemplateBoardModels.reduce((acc: number[], model: Board) => {
			acc.push(...model.tags);
			return acc;
		}, []);
		return this.mmTagsService.getTagCountList(tags);
	}

	// ------ Requests ------

	_boardsRequested = false;
	_boardsRequestPromise: Promise<void> | null = null;
	requestBoards(throwOnFail = false): Promise<void> {
		const variablePrefix = 'boards';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => this.requestService.storeFindAll(ModelName.BOARD),
			undefined,
			undefined,
			throwOnFail,
		);
	}

	// ---------- requested boards cache START ------------

	maxBoardIdsToRemember = 3;

	@tracked boardsRequested: RequestedBoardData[] = [];
	@tracked boardsRequestingData: { id: string; promise: Promise<unknown> }[] = [];

	isBoardDataRequested(id: string) {
		return !!id && this.boardsRequested.some((requestedBoard) => requestedBoard.id === id);
	}

	isBoardDataRequesting(id: string) {
		return !!id && !!this.boardsRequestingData.find((r) => r.id === id);
	}

	requestBoardData(id: string, skipDataRequest = false) {
		let promise;

		// first check if already requested
		if (this.isBoardDataRequested(id)) {
			// we update the requested timestamp, that will be used for sorting out the oldest board
			const indexToUpdate = this.boardsRequested.findIndex(
					(requestedBoard) => requestedBoard.id === id,
				),
				updatedRequestedBoard: RequestedBoardData = {
					id,
					requestedTimestamp: Date.now(),
				};

			if (indexToUpdate !== -1) {
				// update the object at the found index with the new data
				this.boardsRequested[indexToUpdate] = updatedRequestedBoard;
			}

			promise = Promise.resolve();
		} else {
			// check if requesting is in progress
			promise = this.boardsRequestingData.find((r) => r.id === id)?.promise;

			if (!promise) {
				// this request returns only blocks and metricsettings (not samples)
				promise = this.requestService
					.api<RecordData.Payload>('GET', `/boards/${id}`)
					.then((reply) => {
						// data request triggers samples that come over pusher dataUpdated / bgEvent
						// so we need to be subscribed to the channel before making the request
						this.markBoardRequestedAndSubscribeToDataChannel(id)
							?.then((channel) => {
								if (!channel) {
									// usually an expired session or a dropped connection: un-mark the board so the
									// next visit subscribes and requests its data again, instead of staying empty
									this.markBoardNotRequested(id);
									this.loggerService.logError(
										'Failed to subscribe to board data channel',
										{
											boardId: id,
										},
										Severity.ERROR,
									);
									return;
								}
								if (!skipDataRequest) {
									void this.requestService
										.api<RecordData.Payload>('GET', `/boards/${id}/data`)
										.then((payload) => {
											this.storeService.pushPayload(payload);
										});
								}
							})
							.catch(
								this.requestService.handleGenericThrownError.bind(
									this.requestService,
								),
							);

						// markBoardRequested must be called before pushing to store
						this.storeService.pushPayload(reply);
					})
					.finally(() => {
						this.boardsRequestingData = this.boardsRequestingData.filter(
							(r) => r.id !== id,
						);
					})
					.catch(this.requestService.handleGenericThrownError.bind(this.requestService));

				this.boardsRequestingData = [...this.boardsRequestingData, { id, promise }];
			}
		}

		return promise;
	}

	markBoardRequestedAndSubscribeToDataChannel(boardId: string) {
		this.markBoardRequested(boardId);

		return this.socketService.afterDataChannelSubscribed?.then(() => {
			// board data will be sent over board specific pusher channel
			return this.socketService.subscribeToBoardData(+boardId);
		});
	}

	markBoardRequested(id: string) {
		if (!id) {
			return;
		}

		this.boardsRequested = [
			...this.boardsRequested,
			{
				id,
				requestedTimestamp: Date.now(),
			},
		];

		this.checkMaxBoardIdsToRemember();
	}

	markBoardNotRequested(id: string) {
		if (!id) {
			return;
		}

		this.removeIdFromBoardsRequested(id);
	}

	checkMaxBoardIdsToRemember() {
		// check if we should unload some board data (perf reasons)
		if (this.boardsRequested.length > this.maxBoardIdsToRemember) {
			const oldestToNewest = [...this.boardsRequested].sort(
				(a, b) => a.requestedTimestamp - b.requestedTimestamp,
			);

			const boardsToRemove = oldestToNewest.slice(
				0,
				this.boardsRequested.length - this.maxBoardIdsToRemember,
			);

			boardsToRemove.forEach((board) => {
				this.unloadRequestedBoard(board.id);
			});
		}
	}

	unloadRequestedBoard(id: string) {
		this.removeIdFromBoardsRequested(id);
		const garbageCollector = this.garbageCollectorService;
		if (garbageCollector) {
			garbageCollector.clearConnectedRecords('board', { id });
		}
	}

	private removeIdFromBoardsRequested(id: string) {
		this.socketService.unsubscribeFromBoardData(+id);
		this.boardsRequested = this.boardsRequested.filter(
			(requestedBoard) => requestedBoard.id !== id,
		);
	}

	@tracked isRecentlyDeletedLoading = false;
	requestDeletedBoards() {
		this.isRecentlyDeletedLoading = true;
		return this.requestService
			.api<APIData.Boards.RecentlyDeleted.Response>('GET', '/boards', {
				onlyDeleted: true,
			})
			.then((response) => {
				this.storeService.pushPayload(response);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService))
			.finally(() => {
				this.isRecentlyDeletedLoading = false;
			});
	}

	// ---------- requested boards cache END ------------

	editBoard(board: Board): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.navigationService.transitionToRoute('designer.board', board.model);
	}

	openPreview(
		model: Board,
		trackingOrigin: TrackingOriginTypeForPreview | null = 'page',
		boardsCollection: Board[] = [],
		onPopupClose?: () => void,
		onBoardsDataLoaded?: () => void,
	): void {
		void this.openPreviewAndGetPopup(
			model,
			trackingOrigin,
			boardsCollection,
			onPopupClose,
			onBoardsDataLoaded,
		);
	}

	// openPreview, for a caller that waits on the popup: it opens once a preview already open has closed.
	openPreviewAndGetPopup(
		model: Board,
		trackingOrigin: TrackingOriginTypeForPreview | null = 'page',
		boardsCollection: Board[] = [],
		onPopupClose?: () => void,
		onBoardsDataLoaded?: () => void,
	): Promise<PopupModal> {
		this.analyticsService.event(TrackingEvent.DATABOARD_SHOW_PREVIEW, {
			databoard_id: model.numericId,
			name: model.name,
			datasources: model.specificSourceTypesKeys,
			origin: trackingOrigin,
		});

		const props: PopupData.Board.Preview.Any = {
			board: model,
			trackingOrigin,
			isSwitcherShown: boardsCollection.length > 1,
			onSwitchBoard: (currentBoard, direction: PaginationDirection) => {
				return getPreviousOrNextItemByDirection(
					boardsCollection,
					currentBoard as Board,
					direction,
				);
			},
			onBoardsDataLoaded,
		};

		let beforeOpen: Promise<unknown>;
		const existingPreviewPopup = this.popupService.findByComponent(BoardPopupPreviewComponent);
		if (existingPreviewPopup) {
			// if already open, close the previous and open a new one
			beforeOpen = existingPreviewPopup.close();
		} else {
			beforeOpen = Promise.resolve();
		}

		return beforeOpen.then(() => {
			const popup = this.createAndOpenPreviewPopup(props);

			if (onPopupClose) {
				popup.on(PopupEvent.CLOSING, this, () => {
					onPopupClose();
				});
			}

			return popup;
		});
	}

	isBoardPreviewPopupOpen() {
		return this.popupService.isOpen(BoardPopupPreviewComponent);
	}

	createAndOpenPreviewPopup(props: PopupData.Board.Preview.Any) {
		const isWallView = window.innerWidth >= MD_BREAKPOINT;
		const extraClasses: string[] = [isWallView ? 'wall' : 'mobile'];
		if (props.isSwitcherShown) {
			extraClasses.push('show-switcher');
		}

		const popup = this.popupService.createModal(BoardPopupPreviewComponent, {
			popupClass: `popup-type-board-preview ${extraClasses.join(' ')}`,
			isAutoCentered: false,
			isAnimated: false,
			allowStacked: true, // more context, share, tour bubbles, ..
			showClose: !this.flowTourOnboardingService.isBannerShown,
			closeOnOverlayClick: !this.flowTourOnboardingService.isBannerShown,
		});

		popup.open(props);

		return popup;
	}

	checkToOpenAccountTemplateEditPopup() {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.normalBoardModels.length) {
			this.infolayerService.error(
				'To create an account template please create a dashboard first.',
			);
			return;
		}

		this.openAccountTemplateEditPopup(null, 'account_templates');
	}

	openAccountTemplateEditPopup(
		model: Board | null,
		trackingOrigin: TrackingOriginForTemplateCreated,
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}
		const modelCopy = model ? this.createModelFromRecord(model.model) : null;
		this.mmTemplatesService.openAccountTemplateEditPopup(
			TemplateObjectType.BOARD,
			modelCopy,
			trackingOrigin,
		);
	}

	openPublicTemplateEditPopup(model: Board): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		// make sure templates are loaded
		void this.mmTemplatesService.requestBoardPublicTemplates().then(() => {
			this.mmTemplatesService.openPublicTemplateEditPopup(model);
		});
	}

	checkIfCanUseTemplate(isPublicTemplate: boolean, openWarning = false): boolean {
		let actionPassesLimitations = false;

		if (this.billingService.checkIfCan(BillingFeature.BOARDS, openWarning)) {
			if (
				isPublicTemplate ||
				this.billingService.checkIfCan(BillingFeature.ACCOUNT_TEMPLATES, openWarning)
			) {
				actionPassesLimitations = true;
			}
		}

		return actionPassesLimitations;
	}

	requestCreateBoardFromTemplate(
		templateModel: Template,
		data: TemplateResolve.NormalizedResolvePayload,
		trackOrigin = '',
		{
			isInfolayerShown = true,
			isTrackingDisabled = false,
		}: {
			isInfolayerShown?: boolean;
			isTrackingDisabled?: boolean;
		} = {},
	) {
		const fromId = templateModel.numericId!;
		let layer: InfolayerItemGeneral | null = null;
		if (isInfolayerShown) {
			layer = this.infolayerService.loading('Building dashboard...');
		}

		return this.mmTemplatesService
			.useTemplate(templateModel, data, trackOrigin as TrackingOriginForTemplateUsed)
			.then(
				(reply: APIData.Templates.UseBoardTemplateResponse) => {
					const boardData = reply.boards?.[0];
					if (boardData) {
						const boardModel = this.normalBoardModels.find(
							(item) => item.id === String(boardData.id),
						);

						if (layer) {
							showTemplateCreationResult(layer, reply, 'Dashboard');
						}

						if (!isTrackingDisabled) {
							this.triggerDataboardCreated({ from: trackOrigin }, boardModel);
						}

						this.onboardingService.triggerEventAllSetAfterFirstBoardCreated();

						if (boardModel) {
							const boardRecord = boardModel.model;

							boardRecord.newBadge = true;

							return boardRecord;
						}
					} else {
						if (layer) {
							layer.error('Error building dashboard');
						}
						this.loggerService.logError(
							'[bgEvent] create board',
							{
								fromId,
								response: JSON.stringify(reply),
							},
							Severity.INFO,
						);
					}

					return null;
				},
				(errorData) => {
					if (layer) {
						layer.close();
					}

					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	createBoardFromTemplate(
		templateModel: Template,
		trackOrigin = '',
		afterCreated?: (board: BoardRecord) => void,
		onResolveModalClose?: () => void,
		sourcesMap: SourcesMap = {},
		skipResolvePopup = false,
		{
			isInfolayerShown = true,
			isTrackingDisabled = false,
		}: {
			isInfolayerShown?: boolean;
			isTrackingDisabled?: boolean;
		} = {},
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const onAllResolved = (data?: TemplateResolve.NormalizedResolvePayload) => {
			void this.requestCreateBoardFromTemplate(
				templateModel,
				data ?? { dataSourceTypeMap: {} },
				trackOrigin,
				{
					isInfolayerShown,
					isTrackingDisabled,
				},
			).then((boardRecord) => {
				if (boardRecord && afterCreated) {
					afterCreated(boardRecord as BoardRecord);
				}
			});
		};

		if (this.checkIfCanUseTemplate(templateModel.isPublic, true)) {
			if (skipResolvePopup) {
				onAllResolved({ dataSourceTypeMap: sourcesMap });
			} else {
				this.mmTemplatesService.openResolvePopup(
					templateModel,
					onAllResolved,
					trackOrigin,
					onResolveModalClose,
				);
			}
		}
	}

	afterCreateBoardFromTemplate(boardRecord: BoardRecord) {
		const afterTransition = () => {
			const boardModel = this.createModel(boardRecord);
			this.openPreview(boardModel, null);
		};

		this.navigationService.transitionToRoute('boards.index').then(afterTransition);
	}

	createAndOpenNewNotificationForModel(model: Board, trackingOrigin: TrackingOriginType) {
		this.mmNotificationsService.createNewForFeature(
			NotificationCreateType.BOARD,
			model,
			trackingOrigin,
		);
	}

	openScheduleSnapshotModal(board: Board, trackingOrigin: TrackingOriginType): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.mmBoardSharesService.createAndEditForBoard(board, trackingOrigin);
	}

	downloadSnapshotWithInfolayer(board: Board) {
		const infolayer = this.infolayerService.boardSnapshot(board);

		return this.requestService
			.api<APIData.Snapshot.Response>('POST', `/boards/${board.id}/snapshoot`, null, {
				timeout: 2 * 60 * 1000,
			})
			.then((response) => {
				infolayer.setDone(response);
			})
			.catch((e: RequestError) => {
				infolayer.setError('Error generating snapshot');
				this.requestService.handleGenericThrownError(e);
			});
	}

	openMigrationTool(board: Board): void {
		this.popupService
			.createModal(BoardPopupMigrationToolComponent, {
				popupClass: 'popup-migration-tool',
				allowStacked: true, // can open forbidden action and free trial request popups
			})
			.open({
				model: board,
			});
	}

	migrateToGA4(
		boardId: number,
		migrationMode: MigrationMode,
		mappings: MigrationMap[],
	): Promise<MigrationResponse> {
		return this.requestService
			.apiWithAsyncReply<APIData.Migrate.BoardReply>(
				'POST',
				`/migrate/board`,
				{
					boardId,
					transitionSetting: migrationMode,
					mappings,
				},
				{ withoutPrefix: true },
			)
			.then((reply) => {
				const boardId = reply?.board?.id,
					successfullyMigrated = reply?.successfullyMigrated,
					unsuccessfullyMigrated = reply?.unsuccessfullyMigrated;

				if (boardId) {
					const board = this.findModel(String(boardId));
					if (board && successfullyMigrated && unsuccessfullyMigrated) {
						return Promise.resolve({
							board,
							successfullyMigrated,
							unsuccessfullyMigrated,
						});
					}
				}
				return Promise.reject();
			});
	}

	addToReport(
		model: Board,
		trackingOrigin: TrackingOriginTypeForAddReport = 'page',
		onSave?: (reportName: string, reportId: string) => void,
		options?: { openInNewTab?: boolean },
	): PopupModal | undefined {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.billingService.reportsBasicEnabled) {
			this.billingService.handleLimitations(BillingFeature.REPORTS_BASIC);
			return;
		}

		const popupBoardToReport = this.popupService.createModal(
			ReportsPopupBoardToReportComponent,
		);
		popupBoardToReport.open({
			model,
			trackingOrigin,
			onSubmit: async (reportId: string) => {
				const layer = this.infolayerService.loading('Adding dashboard to report...');
				return this.mmReportSlidesService.createFromBoard(model.numericId!, +reportId).then(
					() => {
						this.mmReportsService.triggerReportEdited(trackingOrigin);
						layer.ok('Dashboard added to report!');

						const targetReportModel = this.mmReportsService.findModel(reportId)!;

						onSave?.(targetReportModel.title, reportId);
						this.navigationService.createContextSwitchModal().open({
							actionType: ContextSwitchAction.ADD_TO,
							sourceItem: model,
							targetItem: targetReportModel,
							redirectUrl: options?.openInNewTab
								? this.navigationService.getAbsoluteUrlForRoute(
										'reports.editor.report',
										reportId,
									)
								: undefined,
							onSwitch: () => {
								this.navigationService.transitionToRoute(
									'reports.editor.report',
									reportId,
								);
							},
						});
					},
					(err: RequestError) => {
						layer.close();
						throw err;
					},
				);
			},
		});

		return popupBoardToReport;
	}

	addToLoop(
		model: Board | Board[],
		trackingOrigin: TrackingOriginTypeForAddLoop = 'page',
		onSave?: (loopName: string, loopId: string) => void,
		options?: { openInNewTab?: boolean },
	): Promise<void> {
		return new Promise((resolve, reject) => {
			if (this.accountService.handleDemo()) {
				reject();
				return;
			}
			const popupBoardToLoop = this.popupService.createModal(
				BoardLoopsPopupBoardToLoopComponent,
			);
			popupBoardToLoop.open({
				model,
				trackingOrigin,
				onSubmit: (boardLoopId: string) => {
					const layer = this.infolayerService.loading('Adding dashboard to loop...');

					const boardLoop = this.mmBoardLoopsService.findModel(boardLoopId);

					if (!boardLoop) {
						throw new Error('Looped Databoards not found!');
					}

					const boards = [];
					if (model instanceof Board) {
						boards.push(model);
					} else {
						boards.push(...model);
					}

					const boardIds = boards
						.map((board) => board.numericId)
						.filter((numericId) => typeof numericId === 'number');

					boardLoop.boardIds = uniq([...boardLoop.boardIds, ...boardIds]);

					boardLoop.save().then(
						() => {
							layer.ok('Dashboard added to loop!');

							onSave?.(boardLoop.title, boardLoopId);

							resolve();

							if (boardLoop.canCurrentUserEditSomeBoard) {
								this.navigationService.createContextSwitchModal().open({
									actionType: ContextSwitchAction.ADD_TO,
									sourceItem: model,
									targetItem: boardLoop,
									redirectUrl: options?.openInNewTab
										? this.navigationService.getAbsoluteUrlForRoute(
												'designer.board',
												String(boardLoop.boardsEditableByUser[0]!.model.id),
											)
										: undefined,
									onSwitch: () => {
										this.mmBoardLoopsService.openInDesigner(boardLoop);
									},
								});
							}
						},
						(err: RequestError) => {
							layer.close();
							reject();
							throw err;
						},
					);

					return Promise.resolve();
				},
			});
		});
	}

	// We can pass formatted datasources from outside to trackData or we can get datasources from board model
	triggerDataboardCreated(
		trackData: { from: string; datasources?: (string | undefined)[] },
		model?: Board,
	): void {
		const data: { from: string; datasources: (string | undefined)[]; name?: string } = {
			...trackData,
			datasources: trackData.datasources ?? [],
		};

		if (model) {
			if (!data.datasources.length) {
				// get datasources from board only if not explicitly passed
				data.datasources = model.specificSourceTypesKeys;
			}

			data.name = model.name;
		}

		this.analyticsService.event(TrackingEvent.DATABOARD_CREATED, data);
	}

	duplicateBoard(model: Board, onDuplicate?: (board: Board) => void): Promise<void> {
		const fromBoardId = model.id,
			layer = this.infolayerService.loading('Copying dashboard...');

		return this.requestService
			.api<APIData.Board.RecordResponse>('POST', '/boards/' + fromBoardId + '/clone', null, {
				timeout: 60 * 1000,
			})
			.then(
				(reply) => {
					const oldBoard = this.normalBoardModels.find(
						(item) => item.id === String(fromBoardId),
					);
					layer.ok(`Dashboard <strong>${oldBoard?.name}</strong> copied!`);
					this.storeService.pushPayload(reply);

					const createdRecordId = reply.board?.id;
					let createdBoard;
					if (createdRecordId) {
						createdBoard = this.findModel(String(createdRecordId));
					}

					if (createdBoard) {
						createdBoard.model.newBadge = true;
						if (onDuplicate) {
							onDuplicate(createdBoard);
						}
					}
				},
				(errorData: RequestError) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	createBoardFromMetrics(metrics: Metric[]): Promise<BoardRecord> {
		return new Promise((resolve1, reject1) => {
			if (!this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
				reject1();
			}

			this.createNewModel()
				.save()
				.then((boardRecord) => {
					return new Promise<BoardRecord>((resolve2, reject2) => {
						next(() => {
							this.mmMetricsService
								.batchCreateBlocksFromMetrics(metrics, Number(boardRecord.id))
								.then(() => {
									resolve2(boardRecord);
								})
								.catch(reject2);
						});
					}).then(resolve1);
				})
				.catch(reject1);
		});
	}

	showBoardChoice(metrics: Metric[], from = 'metric'): Promise<number> {
		const getDatasources = () => {
			const spaceAccessIds: (string | number)[] = [];
			metrics.forEach((metric) => {
				if (metric.spaceAccessId) {
					spaceAccessIds.push(metric.spaceAccessId);
				}
			});

			const datasources: string[] = [];
			spaceAccessIds.forEach((spaceAccessId) => {
				const sourceTypeKey = this.mmSourcesService.getSourceTypeKey(String(spaceAccessId));
				if (sourceTypeKey && !datasources.includes(sourceTypeKey)) {
					datasources.push(sourceTypeKey);
				}
			});

			return datasources;
		};

		return new Promise((resolve, reject) => {
			// variable prevents rejecting fullfiled promise onClose (which is always called - regardless of promise
			// being fullfiled or rejected)
			let isPending = true;

			const popup = this.popupService.createModal(BoardPopupChooseComponent, {
				popupClass: 'popup-type-create-block title-bigger',
			});

			popup.open({
				onBoardSelect: (boardIdString: string) => {
					const boardId = +boardIdString;
					if (boardId === -1) {
						if (!this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
							reject();
							return;
						}
						const board = this.createNewModel();
						const layer = this.infolayerService.loading('Creating dashboard...');
						board
							.save()
							.then((b: BoardRecord) => {
								const board = this.findModel(String(b.id));

								if (board) {
									// const datasources = board.specificSourceTypesKeys; // Always empty
									const datasources = getDatasources();
									this.triggerDataboardCreated({ from, datasources }, board);
								}

								layer.close();
								resolve(+b.id!);
							})
							.catch((err: RequestError) => {
								layer.close();
								this.requestService.showAllValidationErrors.bind(
									this.requestService,
								);
								reject(err);
							});
					} else {
						resolve(boardId);
					}
					isPending = false;
				},
				requiredFreeSpaces: metrics.length,
			});

			popup.on(PopupEvent.CLOSING, this, () => {
				// if isPending is set to false, board has been selected and request is still in progress
				// so result of it is not yet known otherwise, no request has been made and promise can safely be rejected
				if (isPending) {
					reject();
				}
			});
		});
	}

	openVisualSettingsPopup(model: Board) {
		this.popupService
			.createModal(BoardPopupVisualSettingsComponent, {
				popupClass: 'wallvisualpopup',
				allowStacked: true, // for image-upload/popup
			})
			.open({ model });
	}

	openBoardDeleteConfirmPopup(props: PopupData.Board.ConfirmDelete) {
		const popup = this.popupService.createModal(BoardPopupDeleteConfirmComponent, {
			popupClass: 'popup-type-confirm title-bigger',
			showClose: false,
		});
		popup.open(props);
	}

	openSharePopup(props: PopupData.Board.Share) {
		this.popupService
			.createModal(BoardPopupShareComponent, {
				popupClass: 'popup-type-board-share title-bigger',
				allowStacked: true, // used because of: common/popups/confirm (shearable link), form/range/popup (send to email tab)
			})
			.open(props);
	}

	openResolveDataPopup(props: PopupData.Board.Resolve) {
		const popup = this.popupService
			.createModal(BoardPopupResolveDataComponent, {
				popupClass: 'popup-type-board-resolve-data',
				allowStacked: true, // source/connect/popup
			})
			.open(props);

		return popup;
	}

	openTransferPopup(
		model: Board | Board[],
		showSpaceId = false,
		toAgency = false,
	): Promise<void> {
		return new Promise((resolve, reject) => {
			if (this.accountService.handleDemo()) {
				reject();
				return;
			}
			const popup = this.popupService.createModal(BoardPopupTransferComponent, {
				popupClass: 'popup-type-board-transfer title-bigger',
			});

			popup.open({
				model,
				showSpaceId,
				toAgency,
			});

			let wasConfirmed = false;
			popup.on('confirm', this, () => {
				wasConfirmed = true;
			});

			popup.on(PopupEvent.CLOSING, this, () => {
				if (wasConfirmed) {
					resolve();
				} else {
					reject();
				}
			});
		});
	}

	resolveSources(
		model: Board,
		origin = 'homeBoard',
		alwaysUseResolveDataPopup = false,
		onResolveAll?: () => void,
	) {
		if (model.isSingleSourceToConnect && !alwaysUseResolveDataPopup) {
			const connectData = model.getDataForSingleSourceConnect();

			if (!connectData) {
				return;
			}

			void this.connectService
				.connectSourceFromIdOrKey(connectData.source.key, { origin })
				.then((connectResult) => {
					if (connectData && connectResult.isSuccess) {
						const source = connectResult.source;
						const data = { dataSourceTypeMap: {} as SourcesMap };
						data.dataSourceTypeMap[connectData.source.id!] = String(source.id); // a loaded source type

						this.requestService
							.apiWithAsyncReply(
								'POST',
								`/resolution-center/${connectData.msgId}`,
								data,
							)
							.then(() => {
								if (onResolveAll) {
									onResolveAll();
								}
							})
							.catch(
								this.requestService.showAllValidationErrors.bind(
									this.requestService,
								),
							);
					}
				})
				.finally(() => {
					this.eventManagerService.trigger(AppEvent.CONNECT_INITIALIZATION_FINISHED);
				});
		} else {
			this.eventManagerService.trigger(AppEvent.CONNECT_INITIALIZATION_FINISHED);
			this.openResolveDataPopup({ model, onResolveAll });
		}
	}

	openBoardAccessTypePopup(model: Board) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const popup = this.popupService.createModal(CommonPopupAccessPermissionsComponent, {
			popupClass: 'popup-type-access-permissions',
			allowStacked: true,
		});
		popup.open({ model });
		return popup;
	}

	openDuplicatePopup(
		model: Board,
		askRedirect: boolean,
		trackOrigin: TrackingOriginForDataboardDuplicate,
		onDuplicate?: (board: Board) => void,
	) {
		const popup = this.popupService.createModal(BoardPopupDuplicateComponent, {
			popupClass: 'popup-type-board-duplicate title-bigger',
		});
		popup.open({ model, askRedirect, trackOrigin, onDuplicate });
		return popup;
	}

	openEnableCustomDataUpdatePopup(model: Board, trackingOrigin?: string) {
		if (this.navigationService.isDatawallApp) {
			this.navigationService.openPreviewBoardWithLimitation(model.boardIdForPublicUrl);
		} else {
			this.mmSourcesService.openEnableCustomDataUpdatePopup(
				model.sourcesObjs,
				false,
				trackingOrigin,
			);
		}
	}

	createBoardsSelectListModal() {
		return this.popupService.createModal(BoardsPopupSelectListComponent, {
			popupClass: 'popup-type-boards-select-list popup--fixed-size',
			allowStacked: true, // used because of: common/popups/popup-list (filter-sort)
		});
	}

	openDesigner(
		tagToAdd?: number,
		trackingOrigin: TrackingOriginForDataboardCreatedNew = 'designer',
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.DATABOARD_CREATED_NEW, {
			origin: trackingOrigin,
		});
		this.navigationService.transitionToRoute('designer').then(() => {
			if (tagToAdd) {
				this.wizardService.tagForNewBoard = tagToAdd;
			}
		});
	}

	openWizard(tagToAdd?: number, trackingOrigin: TrackingOriginForDataboardCreatedNew = 'wizard') {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.DATABOARD_CREATED_NEW, {
			origin: trackingOrigin,
		});
		this.navigationService.transitionToRoute('wizard').then(() => {
			if (tagToAdd) {
				this.wizardService.tagForNewBoard = tagToAdd;
			}
		});
	}

	getColorGradient(color: string): string {
		return getGradient(color, this.constantsService.boardColors);
	}

	getRandomColor(includeLight = false): string {
		let colors = this.constantsService.boardColors;
		if (!includeLight) {
			colors = colors.filter((item) => !item.isLight);
		}
		const hexColors = colors.map((item) => item.hex);
		return hexColors[Math.floor(Math.random() * hexColors.length)]!;
	}

	availableThemesForColor(color: string): number[] {
		let themes = [];

		const selectedColorData = this.constantsService.boardColors.find(
			(item) => item.hex === color,
		);

		if (selectedColorData) {
			themes = selectedColorData.themes;
		} else {
			themes = ThemeConfig.map((item) => item.id);
		}

		return themes;
	}

	getBgTheme(color: string, themeId: number): Theme.LIGHT | Theme.DARK {
		// light vs dark font & graphs
		const colorObj = this.constantsService.boardColors.find((item) => item.hex === color);

		let bgTheme = Theme.DARK;
		if (colorObj?.isLight) {
			bgTheme = Theme.LIGHT;
		}

		if (!colorObj && themeId === 5) {
			// custom color with greyscale black - must take light bgtheme
			bgTheme = Theme.LIGHT;
		}

		return bgTheme;
	}

	getColorConfig(bgColor: string, themeId = 1): BlockThemeConfig.Parsed {
		const themeConfig = ThemeConfig.find((item) => item.id === themeId) || ThemeConfig[0]!,
			bgTheme = this.getBgTheme(bgColor, themeId),
			isDark = bgTheme === Theme.DARK,
			white = '#ffffff',
			black = '#000000',
			baseColor = isDark ? white : black,
			inverseColor = isDark ? black : white;

		return {
			themeId,
			bgTheme,
			bgColor,
			baseColor,
			inverseColor,
			colors: themeConfig.colors,
			trafficLightsColors: themeConfig.trafficLightsColors,
		};
	}

	getUrlWithPreviewHash(
		baseUrl: string,
		previewHash: BoardPreviewMap = {},
		isIframe = false,
	): string {
		const hashPieces: string[] = [];

		let url = baseUrl;

		Object.entries(previewHash).forEach((entry) => {
			const boardId = entry[0],
				hash = entry[1];
			if (hash) {
				hashPieces.push(`${boardId}=${hash}`);
			}
		});

		if (hashPieces.length) {
			url += '?' + hashPieces.join('&');
			if (isIframe) {
				url += '&i';
			}
		} else if (isIframe) {
			url += '?i';
		}

		return url;
	}

	restore(model: Board) {
		if (!this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			return;
		}
		return this.requestService
			.api<APIData.Boards.Restore.Response>('POST', '/boards/restore', {
				boardId: model.id,
			})
			.then((response) => {
				this.storeService.pushPayload(response);
				this.infolayerService.ok(
					`<strong>${model.name}</strong> dashboard successfully restored.`,
				);
				this.triggerDataboardCreated({ from: 'recently_deleted_restore' }, model);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	// ------ Image size fetching ------
	// used for Boards::Share::Schedule::IncludeBgImages for reports, boards, loops to estimate image size
	// keep it in one place to avoid multiple requests for same resource
	private cdnImagesSizes: Record<string, Promise<number> | number> = {};

	private getCdnImageSize(imageUrl: string): Promise<number> {
		const cachedSizeOrPromise = this.cdnImagesSizes[imageUrl];
		if (typeof cachedSizeOrPromise === 'number') {
			return Promise.resolve(cachedSizeOrPromise);
		} else if (cachedSizeOrPromise) {
			return cachedSizeOrPromise;
		} else {
			const promise = getResourceSizeFromUrl(imageUrl);
			this.cdnImagesSizes[imageUrl] = promise;
			void promise.then((size) => {
				this.cdnImagesSizes[imageUrl] = size;
			});
			return promise;
		}
	}

	async getCdnImageSizes(imageUrls: string[]) {
		let bytes = 0;

		await Promise.allSettled(
			imageUrls.map((image) => {
				return this.getCdnImageSize(image).then((size) => {
					bytes += size;
				});
			}),
		);

		return bytes;
	}

	// ------ Batch actions ------

	override get confirmWord(): string | undefined {
		return;
	}

	override batchFavorite(items: Board[]) {
		return super.batchFavorite(items).then(() => {
			return this.updateOrderAfterFavoriteToggle();
		});
	}

	override batchUnfavorite(items: Board[]) {
		return super.batchUnfavorite(items).then(() => {
			return this.updateOrderAfterFavoriteToggle();
		});
	}

	updateOrderAfterFavoriteToggle(): Promise<void> {
		const allBoards = sortBy(this.boardModels, 'order', 'numericId');

		const favorites: Board[] = [];
		const nonFavorites: Board[] = [];

		allBoards.forEach((board: Board) => {
			if (board.favorite) {
				favorites.push(board);
			} else {
				nonFavorites.push(board);
			}
		});

		const newOrderedBoards = [...favorites, ...nonFavorites];
		const newOrderIds = newOrderedBoards.map((board) => board.numericId!);

		newOrderedBoards.forEach((board, index) => {
			board.order = index;
			board.applyBufferedChanges(['order']);
		});

		return this.requestService
			.api<RecordData.Payload>('POST', '/boards/order', newOrderIds)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	batchAddToReport(items: Board[], reportId: number) {
		const objectIds = items.map((board) => board.numericId!);
		return this.batchAction(BatchSelectActionType.ADD_TO_REPORT, [reportId], objectIds);
	}

	batchCopyToSpace(items: Board[], spaceIds: number[]) {
		const objectIds = items.map((board) => board.numericId!);
		return this.batchAction(BatchSelectActionType.TRANSFER_TO_CLIENT, objectIds, spaceIds);
	}

	batchHideOnMobile(items: Board[]) {
		const objectIds = items.map((board) => board.numericId!);
		return this.batchAction(BatchSelectActionType.HIDE_ON_MOBILE, objectIds);
	}

	batchShowOnMobile(items: Board[]) {
		const objectIds = items.map((board) => board.numericId!);
		return this.batchAction(BatchSelectActionType.UNHIDE_ON_MOBILE, objectIds);
	}

	override batchDelete(items: Board[]) {
		return super.batchDelete(items).then(() => {
			this.flowPromotionsService.startRecentlyDeletedTourForBoards();
		});
	}

	batchDeletePermanently(items: Board[]) {
		return super.batchDelete(items);
	}

	batchRestore(items: Board[]) {
		return super.batchRestoreAction(items).then(() => {
			items.forEach((item) => {
				item.deleted = null;
			});
		});
	}

	openBatchAddToReportPopup(
		items: Board[],
		trackingOrigin: TrackingOriginTypeForAddReport = 'page',
	) {
		return new Promise((resolve, reject) => {
			if (this.accountService.handleDemo()) {
				reject();
				return;
			}

			if (!this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, true)) {
				reject();
				return;
			}

			const popupBoardToReport = this.popupService.createModal(
				ReportsPopupBoardToReportComponent,
			);
			popupBoardToReport.open({
				model: items,
				trackingOrigin,
				onSubmit: async (reportId: string) => {
					return this.batchAddToReport(items, +reportId)
						.then((reply) => {
							this.mmReportsService.triggerReportEdited(trackingOrigin);

							const targetReportModel = this.mmReportsService.findModel(reportId)!;
							this.navigationService.createContextSwitchModal().open({
								actionType: ContextSwitchAction.ADD_TO,
								sourceItem: items,
								targetItem: targetReportModel,
								onSwitch: () => {
									this.navigationService.transitionToRoute(
										'reports.editor.report',
										reportId,
									);
								},
							});
							resolve(reply);
						})
						.catch(reject);
				},
			});
		});
	}

	// ------ For-store ------

	modelName = ModelName.BOARD;
	override modelTitle = 'dashboard';

	createModel(model: BoardRecord): Board {
		return new Board({ model });
	}

	override createNewRecord(properties: CreateRecordProperties<BoardRecord> = {}): BoardRecord {
		properties = this.useGlobalVisualSettings(
			properties,
			this.accountService.globalVisualSettingsForBoard,
		);

		if (typeof properties.color === 'undefined') {
			properties.color = this.getRandomColor();
			properties.themeId = this.availableThemesForColor(properties.color)[0] || 1;
		}
		return super.createNewRecord(properties) as BoardRecord;
	}

	// ------ Flow view selected board ID helpers ------

	@tracked flowViewSelectedBoardId: string | null = null;

	getFlowViewSelectedBoardId(): string | null {
		return this.flowViewSelectedBoardId;
	}

	setFlowViewSelectedBoardId(boardId: string | null): void {
		this.flowViewSelectedBoardId = boardId;
	}

	clearFlowViewSelectedBoardId(): void {
		this.flowViewSelectedBoardId = null;
	}

	// ------ Board blocks limitation ------

	private createDatawallFullPopup() {
		return this.popupService.createModal(DatawallPopupFullComponent, {
			popupClass: 'popup-type-datawall-full',
		});
	}

	async toggleVerification(board: Board): Promise<void> {
		const verificationInfo = board.verificationInfo;
		if (!verificationInfo) return;
		await this.verificationService.toggleVerification(
			verificationInfo,
			'databoard',
			board.name,
			(updated) => {
				board.directPropUpdate({ verificationInfo: updated });
			},
		);
	}

	handleFullBoard(model: Board): boolean {
		let isBoardFull = false;

		if (model.freeSpaces < 1 && !this.accountService.isPredefinedBlocksSpace) {
			isBoardFull = true;

			this.createDatawallFullPopup().open();
		}

		return isBoardFull;
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/boards': ModelManagerBoardsService;
	}
}
